import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { WARMUP_RECOMMENDATION } from '@/graphql/warmup-queries'
import { isWarmupObjective } from '@/utils/pixel-warmup'

// Why the recommendation is unavailable although the feature itself is on for the licence
const FEATURE_OFF_REASONS = ['feature_disabled', 'objective_not_supported']

/**
 * What the pixel says about how a new campaign could start (read only), and whether the pixel warm-up features are
 * available to the licence at all. Nothing is requested for objectives other than sales and leads, or while `enabled`
 * is false. A failed read counts as "not available": the wizard then behaves as it did before the feature.
 * @param {{ objective?: string, targetEvent?: string, audienceAdSets?: number, enabled?: boolean }} options
 */
export const useWarmupRecommendation = ({ objective, targetEvent, audienceAdSets, enabled = true }) => {
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : undefined
    const active = enabled && !!campaignId && !!publisherId && isWarmupObjective(objective)

    const { data, previousData, loading, error } = useQuery(WARMUP_RECOMMENDATION, {
        variables: {
            publisherId,
            campaignId,
            objective: String(objective || '').toLowerCase(),
            ...(targetEvent ? { targetEvent } : {}),
            ...(audienceAdSets ? { audienceAdSets } : {}),
        },
        skip: !active,
        errorPolicy: 'all',
    })

    // While a changed question is read, the last answer stays (the section does not flicker away)
    const current = data || (loading ? previousData : null)
    const recommendation = error ? null : current?.warmupRecommendation || null
    const featureAvailable = !!recommendation && !FEATURE_OFF_REASONS.includes(recommendation.unavailable_reason)

    return { recommendation, loading: active && loading, featureAvailable }
}
